import { Alert, AlertDescription, AlertIcon, Button, useToast } from "@chakra-ui/react";
import FormModal from "../../../components/FormModal";
import { useEffect, useState } from "react";
import { v4 } from 'uuid';
import { translate } from "../../../i18n";
import { CreateCourtFormAvailabilityInputType, SLOTS_PER_HOUR, transformInputToRule } from "../../../models/court/dto/court-availability-template.constants";
import { seedAvailabilityFormState } from "../../court-management/utils/seed-availability-form-state";
import { CourtDto, SportType } from "../../../models/court/dto/court.dto";
import AddAvailabilitiesForm from "../../court-management/components/AddAvailabilitiesForm";
import CourtAvailabilityTypePicker from "../../court-management/components/CourtAvailabilityTypePicker";
import { useBatchUpsertAvailabilitiesCompact } from "../../court-management/hooks/useBatchUpsertAvailabilitiesCompact";
import { buildAvailabilitySaveWarning } from "../utils/availability-save-warning";
import { buildCompactAvailabilityBlocks } from "../../court-management/utils/build-compact-availability-blocks";
import { availabilitySubmitBlockerToast, findAvailabilitySubmitBlocker } from "../../court-management/utils/availability-submit-blocker";
import { getDefaultCurrency } from "../../court-pricing-management/components/CurrencyPicker";
type Props = { court: CourtDto };

const getNewDefaultAvailability = (): CreateCourtFormAvailabilityInputType => ({
    id: v4(),
    daysOfWeek: [0, 1, 2, 3, 4, 5, 6],
    min: 8 * SLOTS_PER_HOUR,
    max: 12 * SLOTS_PER_HOUR,
    slotDurationInMs: 60 * 60 * 1000,
    configurations: [{
        id: v4(),
        slotDurationInMs: 60 * 60 * 1000,
        sport: SportType.PADEL,
        price: 0,
        currency: getDefaultCurrency()
    }]
});
export default function AddAvailabilitiesModal({ court }: Props) {
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [initialSeed] = useState(() => seedAvailabilityFormState(court, getNewDefaultAvailability));

    const [isGameAvailable, setIsGameAvailable] = useState(initialSeed.isGameAvailable);
    const [isCoachingAvailable, setIsCoachingAvailable] = useState(initialSeed.isCoachingAvailable);
    // Seeded, not hardcoded: the DB and entity default is `true`, so sending a
    // hardcoded `false` back turned every legacy court's schedule into
    // one-availability-per-config-per-slot on the next save. See
    // `seedAvailabilityFormState`.
    const [returnAsOneChunk, setReturnAsOneChunk] = useState(initialSeed.returnAsOneChunk);
    const [availabilities, setAvailabilities] = useState<CreateCourtFormAvailabilityInputType[]>(
        initialSeed.availabilities,
    );
    const [hasMisencodedRules, setHasMisencodedRules] = useState(initialSeed.hasMisencodedRules);
    const toast = useToast();

    useEffect(() => {
        if (!court?.availabilityTemplates?.length) {
            return;
        }
        // Never re-seed a form the operator is editing. A background refetch
        // used to overwrite their in-progress rules mid-edit; it would now
        // also clear the mis-encoded-hours warning they were acting on. The
        // save closes the modal, so the invalidation that follows still
        // re-seeds — just after the operator is done.
        if (isModalVisible) {
            return;
        }
        // Re-seed EVERY field together. This effect used to restore only the
        // rules, so a court prop arriving (or refetching) after mount left the
        // three flags on whatever was in state — a half-reset form whose save
        // rebuilds a year of availabilities.
        const seed = seedAvailabilityFormState(court, getNewDefaultAvailability);
        setAvailabilities(seed.availabilities);
        setIsGameAvailable(seed.isGameAvailable);
        setIsCoachingAvailable(seed.isCoachingAvailable);
        setReturnAsOneChunk(seed.returnAsOneChunk);
        setHasMisencodedRules(seed.hasMisencodedRules);
        // `isModalVisible` is a real dependency: closing the modal is exactly
        // when a re-seed becomes safe again, so the effect should run then.
    }, [court, isModalVisible]);

    const { mutateAsync: batchUpsertAvailabilities, isLoading } = useBatchUpsertAvailabilitiesCompact(court?.id, (result) => {
        // A price and a capacity can combine into Stripe's 1–49 cent dead zone
        // (€9.00 split twenty ways is 45c a head), and the API then REFUSES
        // every booking at that capacity. The save itself is correct and stays —
        // an operator halfway through a schedule is in a legitimate state — but
        // a plain success toast was the whole defect: the only other way to find
        // out was a player failing to book.
        // Two independent silent outcomes, one sentence: a price nobody can pay
        // (D25) and a size nobody can buy (D136). See `buildAvailabilitySaveWarning`.
        const warning = buildAvailabilitySaveWarning(result);
        if (warning) {
            toast({
                title: translate('availabilities_edited_successfully'),
                description: warning,
                status: 'warning',
                duration: 12000,
                isClosable: true,
                position: 'top-right'
            });
            setIsModalVisible(false);
            return;
        }

        toast({
            title: translate('availabilities_edited_successfully'),
            status: 'success',
            duration: 3000,
            position: 'top-right'
        });
        setIsModalVisible(false);
    }, (error) => {
        // The API rejects this batch with a precise 400 ("Invalid availability
        // pricing capacity 30…", "Duplicate availability pricing for sport…"),
        // and this toast used to drop it on the floor and print "Error". The
        // batch replaces a year of availabilities in one call, so an operator
        // who cannot see WHICH row was refused has nothing to go on.
        toast({
            title: translate('error'),
            description: error instanceof Error ? error.message : undefined,
            status: 'error',
            duration: 8000,
            isClosable: true,
            position: 'top-right'
        });
    });

    const [startDate, setStartDate] = useState(() => {
        const date = new Date();
        date.setHours(0, 0, 0, 0);
        return date;
    });
    const [endDate, setEndDate] = useState(() => {
        const date = new Date(new Date().getTime() + 364 * 24 * 60 * 60 * 1000);
        date.setHours(23, 59, 59, 999)
        return date;
    });

    const onRemoveAvailability = () => {
        setAvailabilities(availabilities.slice(1));
    };
    const onAddAvailability = () => {
        setAvailabilities([...availabilities, getNewDefaultAvailability()]);
    };

    const handleSubmit = () => {
        // Guards, in the shared order: an empty/non-numeric price parses to
        // `undefined` and the API rejects the whole batch (500 pre-guard, 400
        // post-guard); two prices for the same sport + duration + capacity
        // inside one availability block make the price ambiguous and the API
        // rejects it too (a partial unique index backs that at the DB level).
        // Either way the operator gets the offending capacity named rather than
        // a generic failure. Grouped per block — overlapping availabilities may
        // legitimately repeat a triple.
        const blocker = findAvailabilitySubmitBlocker(
            availabilities.map(a => a.configurations),
            availabilities.flatMap(a => a.configurations.map(c => c.price)),
        );
        if (blocker) {
            const { title, durationMs } = availabilitySubmitBlockerToast(blocker);
            toast({
                title,
                status: 'error',
                duration: durationMs,
                position: 'top-right'
            });
            return;
        }

        // Send the compact schedule (a handful of blocks) and let the server
        // expand it into concrete slots. Expanding client-side across a year
        // built tens of thousands of objects and froze/crashed the browser.
        // The server expands in the operator's IANA timezone (sent below), so
        // the resulting instants match the previous browser-local behavior.
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const blocks = buildCompactAvailabilityBlocks(availabilities, {
            sport: SportType.PADEL,
            currency: getDefaultCurrency(),
        });

        batchUpsertAvailabilities({
            timeZone,
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString(),
            isCoachingAvailable,
            isGameAvailable,
            returnAsOneChunk,
            blocks,
            availabilityTemplates: [{
                id: v4(),
                courtId: court.id,
                startDate: startDate,
                endDate: endDate,
                appliedToAllTime: true,
                rules: availabilities.map(a => transformInputToRule(a)),
                isGameAvailable,
                isCoachingAvailable,
                returnAsOneChunk
            }],
        })
    };

    return (
        <>
            <Button width={60} colorScheme="brand" onClick={() => { setIsModalVisible(true) }}>
                {translate('court.add_availabilities')}
            </Button>
            <FormModal
                isOpen={isModalVisible}
                onClose={() => setIsModalVisible(false)}
                title={translate('court.add_availabilities')}
                onSubmit={handleSubmit}
                submitLabel={translate('submit')}
                isSubmitting={isLoading}
                isSubmitDisabled={!isGameAvailable && !isCoachingAvailable}
                size="xl"
            >
                        {hasMisencodedRules && (
                            // Non-blocking on purpose: correcting the hours and
                            // saving IS the remedy, so refusing the save would
                            // leave the court uneditable. See
                            // `isMisencodedTemplateRule` for what is detected
                            // and what it cannot detect.
                            <Alert status="warning" borderRadius="md" mb={4}>
                                <AlertIcon />
                                <AlertDescription fontSize="sm">
                                    {translate('courts.availability_hours_may_be_wrong')}
                                </AlertDescription>
                            </Alert>
                        )}
                        <CourtAvailabilityTypePicker
                            isGameAvailable={isGameAvailable}
                            setIsGameAvailable={setIsGameAvailable}
                            isCoachingAvailable={isCoachingAvailable}
                            setIsCoachingAvailable={setIsCoachingAvailable}
                        />
                        <AddAvailabilitiesForm
                            startDate={startDate}
                            setStartDate={setStartDate}
                            endDate={endDate}
                            setEndDate={setEndDate}
                            onAddAvailability={onAddAvailability}
                            onRemoveAvailability={onRemoveAvailability}
                            availabilities={availabilities}
                            onAvailabilitiesChange={setAvailabilities}
                            returnAsOneChunk={returnAsOneChunk}
                            setReturnAsOneChunk={setReturnAsOneChunk} />
            </FormModal>
        </>
    )
}
